import api from '../lib/api';
import axios from 'axios';

// Create a separate axios instance for public endpoints (no auth interceptors)
const publicApi = axios.create({
  baseURL: '/api',
});

export interface EntityType {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  created_at: string;
}

export interface NoteEntity {
  entity_type: string;
  entity_type_display: string;
  entity_id: string;
  entity_display_name: string;
}

export interface NoteV2 {
  id: string;
  title?: string;
  content: string;
  include_in_reports: boolean;
  created_at: string;
  updated_at: string;
  version: number;
  parent_note_id?: string;
  reply_count?: number;
  thread_depth?: number;
  external_author_name?: string;
  author: {
    username: string | null;
    display_name: string;
  };
  entities: NoteEntity[];
  tags: string[];
}

export interface NoteReply {
  id: string;
  title?: string;
  content: string;
  created_at: string;
  updated_at: string;
  version: number;
  include_in_reports: boolean;
  parent_note_id: string;
  reply_count: number;
  author_name: string;
  external_author_name?: string;
  tags: string[];
}

export interface CreateReplyData {
  title?: string;
  content: string;
  include_in_reports?: boolean;
}

export interface CreateNoteV2Data {
  title?: string;
  content: string;
  include_in_reports?: boolean;
  tags?: string[];
  entities?: {
    entity_type: string;
    entity_id: string;
    entity_display_name?: string;
  }[];
}

export interface UpdateNoteV2Data {
  title?: string;
  content: string;
  include_in_reports?: boolean;
  tags?: string[];
  entities?: {
    entity_type: string;
    entity_id: string;
    entity_display_name?: string;
  }[];
}

export interface NotesV2Filters {
  search?: string;
  tags?: string;
  entity_type?: string;
  entity_id?: string;
  author_id?: string;
  sort?: 'created_at' | 'updated_at' | 'title' | 'relevance' | 'author';
  order?: 'asc' | 'desc';
  include_in_reports?: 'all' | 'true' | 'false';
}

export interface NotesV2Response {
  notes: NoteV2[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface EntityNotesResponse {
  notes: {
    id: string;
    title?: string;
    content: string;
    created_at: string;
    updated_at: string;
    include_in_reports: boolean;
    author_name: string;
    external_author_name?: string;
    tags: string[];
    parent_note_id?: string;
    reply_count?: number;
  }[];
  entity_type: string;
  entity_id: string;
}

// Phase 11: Note Sharing Types

// Individual recipient in a share
export interface ShareRecipient {
  id: string;
  email: string;
  is_user: boolean;
  user_name?: string;
  notification_type: 'email' | 'in_app' | 'both' | 'none';
  email_sent: boolean;
  in_app_notified: boolean;
  viewed_at?: string;
  responded_at?: string;
  status: 'pending' | 'viewed' | 'responded';
  response?: {
    content: string;
    submitted_at: string;
    author_name?: string;
  };
}

// Share with recipients (multi-recipient model)
export interface NoteShare {
  id: string;
  token: string;
  short_id?: string;
  expires_at: string;
  created_at: string;
  revoked_at?: string;
  send_email?: boolean;
  created_by_name: string;
  status: 'pending' | 'viewed' | 'has_responses' | 'all_responded' | 'expired' | 'revoked' | 'active';
  recipient_count: number;
  viewed_count: number;
  responded_count: number;
  recipients: ShareRecipient[];
}

export interface CreateNoteShareData {
  recipient_email?: string;
  recipient_emails?: string[];
  recipients?: Array<{
    email: string;
    notification_type?: 'email' | 'in_app' | 'both' | 'none';
  }>;
  expires_at: string; // ISO 8601 date string
  send_emails?: boolean;
}

// Response when creating a new share (new format)
export interface NoteShareResponse {
  share_id: string;
  token: string;
  short_id?: string;
  share_url: string;
  short_url?: string;
  note_title?: string;
  expires_at: string;
  created_at: string;
  send_emails: boolean;
  recipients: Array<{
    id: string;
    email: string;
    notification_type: string;
    is_user: boolean;
    user_name?: string;
    email_sent: boolean;
    in_app_notified: boolean;
  }>;
}


export interface PublicNoteData {
  share_id: string;
  expires_at: string;
  note: {
    title?: string;
    content: string;
    created_at: string;
    author_name: string;
    entities: NoteEntity[];
  };
  organization: {
    name?: string;
    logo_url?: string;
  };
  shared_by: string;
  existing_response?: {
    content: string;
    submitted_at: string;
    updated_at: string;
  };
}

export interface FeedbackSubmitResponse {
  response_id: string;
  content: string;
  submitted_at: string;
  updated_at: string;
  message: string;
}

/** One entry in a workspace's derived tag vocabulary. */
export interface WorkspaceNoteTag {
  /** Lowercased identity — what the filter matches on. */
  key: string;
  /** Earliest-created spelling, used for display. Stable as counts change. */
  label: string;
  /** How many notes in the workspace carry this tag, in any casing. */
  note_count: number;
}

class NotesV2ApiService {
  /**
   * Get all available entity types
   */
  static async getEntityTypes(): Promise<EntityType[]> {
    const response = await api.get('/v2/entity-types');
    return response.data.data.entity_types;
  }

  /**
   * Get notes for a workspace
   */
  static async getWorkspaceNotes(
    workspaceId: string,
    filters: NotesV2Filters = {},
    page: number = 1,
    limit: number = 50
  ): Promise<NotesV2Response> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '' && value !== undefined)
      )
    });

    const response = await api.get(`/v2/workspaces/${workspaceId}/notes?${params}`);
    return response.data.data;
  }

  /**
   * The workspace's tag vocabulary, derived from tags already in use.
   * Case-insensitive identity; `label` is the earliest-created spelling.
   */
  static async getWorkspaceNoteTags(workspaceId: string): Promise<WorkspaceNoteTag[]> {
    const response = await api.get(`/v2/workspaces/${workspaceId}/notes/tags`);
    return response.data.data.tags;
  }

  /**
   * Create a new note
   */
  static async createNote(workspaceId: string, noteData: CreateNoteV2Data): Promise<NoteV2> {
    const response = await api.post(`/v2/workspaces/${workspaceId}/notes`, noteData);
    return response.data.data.note;
  }

  /**
   * Update an existing note
   */
  static async updateNote(workspaceId: string, noteId: string, noteData: UpdateNoteV2Data): Promise<NoteV2> {
    const response = await api.put(`/v2/workspaces/${workspaceId}/notes/${noteId}`, noteData);
    return response.data.data.note;
  }

  /**
   * Delete a note
   */
  static async deleteNote(workspaceId: string, noteId: string): Promise<void> {
    await api.delete(`/v2/workspaces/${workspaceId}/notes/${noteId}`);
  }

  /**
   * Get notes for a specific entity
   */
  static async getEntityNotes(
    entityType: string,
    entityId: string,
    workspaceId?: string,
    limit: number = 10
  ): Promise<EntityNotesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString()
    });

    if (workspaceId) {
      params.append('workspace_id', workspaceId);
    }

    const response = await api.get(`/v2/entities/${entityType}/${entityId}/notes?${params}`);
    return response.data.data;
  }

  /**
   * Note counts for many entities of one type in a single request (#1179).
   * Missing keys mean zero. Ids are chunked so a long list never exceeds the
   * server cap or a URL length limit.
   */
  static async getEntityNoteCounts(
    workspaceId: string,
    entityType: string,
    entityIds: string[]
  ): Promise<Record<string, number>> {
    const ids = [...new Set(entityIds.map(String).filter(Boolean))];
    const counts: Record<string, number> = {};
    const CHUNK = 200;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const params = new URLSearchParams({
        entity_type: entityType,
        entity_ids: ids.slice(i, i + CHUNK).join(',')
      });
      const response = await api.get(`/v2/workspaces/${workspaceId}/notes/counts?${params}`);
      Object.assign(counts, response.data?.data?.counts ?? {});
    }
    return counts;
  }

  /**
   * Get note count for an entity (derived from getEntityNotes)
   */
  static async getEntityNoteCount(
    entityType: string,
    entityId: string,
    workspaceId?: string
  ): Promise<number> {
    try {
      const response = await this.getEntityNotes(entityType, entityId, workspaceId, 1);
      // We don't have total count in the response, so this is an approximation
      // For exact counts, we'd need a dedicated endpoint
      return response.notes.length;
    } catch (error) {
      console.error('Error getting entity note count:', error);
      return 0;
    }
  }

  /**
   * Validate and get entity info (for autocomplete/validation)
   */
  static async validateEntity(entityType: string, entityId: string): Promise<{
    entity_id: string;
    entity_display_name: string;
    exists: boolean;
  }> {
    // This would require a backend endpoint to validate entities
    // For now, return basic info
    return {
      entity_id: entityId,
      entity_display_name: `${entityType}: ${entityId}`,
      exists: false
    };
  }

  // =========================================
  // NOTE SHARING METHODS (Phase 11)
  // =========================================

  /**
   * Create share with multiple recipients
   * Returns the new format with single token and multiple recipients
   */
  static async createNoteShareMultiRecipient(
    noteId: string,
    recipients: Array<{ email: string; notification_type?: string }>,
    expiresAt: string,
    sendEmails: boolean = true
  ): Promise<NoteShareResponse> {
    const response = await api.post(`/v2/notes/${noteId}/share`, {
      recipients,
      expires_at: expiresAt,
      send_emails: sendEmails
    });
    return response.data.data as NoteShareResponse;
  }

  /**
   * Get all shares for a note
   */
  static async getNoteShares(noteId: string): Promise<NoteShare[]> {
    const response = await api.get(`/v2/notes/${noteId}/shares`);
    return response.data.data.shares;
  }

  /**
   * Revoke a share
   */
  static async revokeShare(shareId: string): Promise<void> {
    await api.delete(`/v2/shares/${shareId}`);
  }

  // =========================================
  // NOTE THREADING METHODS (Phase 11)
  // =========================================

  /**
   * Get replies for a note
   */
  static async getNoteReplies(noteId: string): Promise<NoteReply[]> {
    const response = await api.get(`/v2/notes/${noteId}/replies`);
    return response.data.data.replies;
  }

  /**
   * Create a reply to a note
   */
  static async createReply(noteId: string, replyData: CreateReplyData): Promise<NoteReply> {
    const response = await api.post(`/v2/notes/${noteId}/reply`, replyData);
    return response.data.data.reply;
  }
}

// =========================================
// PUBLIC NOTE FEEDBACK API (No Auth Required)
// =========================================

export class NoteFeedbackApiService {
  private static readonly BASE_URL = '/public/note-feedback';

  /**
   * Get share info and note content for feedback
   * Requires email for verification
   */
  static async getShareInfo(token: string, email: string): Promise<PublicNoteData> {
    const params = new URLSearchParams({ email });
    const response = await publicApi.get(`${this.BASE_URL}/${token}?${params}`);
    return response.data.data;
  }

  /**
   * Submit or update feedback response
   * @param displayName - Required for new responses, shows as the author name on the note
   */
  static async submitFeedback(
    token: string,
    email: string,
    content: string,
    displayName?: string
  ): Promise<FeedbackSubmitResponse> {
    const payload: { email: string; content: string; display_name?: string } = { email, content };
    if (displayName) {
      payload.display_name = displayName;
    }
    const response = await publicApi.post(`${this.BASE_URL}/${token}`, payload);
    return response.data.data;
  }
}

export default NotesV2ApiService;
